// WHAT: Roadmap-Dashboard-Widget mit aggregierten Metriken (Total, Completion, High Priority/Impact)
// WHY:  Gibt auf einen Blick Auskunft über den Umsetzungsstand und die Fokussierung der Roadmap
// DOMAIN: core → dashboard → widgets → admin
// DEPENDENCIES: `useRoadmapItems`-Hook (Admin-Domain), `WidgetContainer`, TracQ Grid/Card/Heading/Text
// RISKS:
//   - NOTE: Berechnungslogik (z. B. `impact_score === 'high'`) hängt direkt von den Roadmap-Typen und Business-Regeln ab

import { BarChart3 } from "lucide-react";
import { type FC, useMemo } from "react";

import { Grid, Text, Heading, Card } from "@/components/ui";
import { useRoadmapItems } from "@/domains/admin/roadmap/hooks/useRoadmapItems";

import { useWidgetConfig } from "../../../hooks/useWidgetConfig";
import { WIDGET_IDS } from "../../../store/widgetRegistry";
import { WidgetContainer } from "../base";

/**
 * WHAT:
 *   Aggregiert grundlegende Roadmap-Kennzahlen wie Anzahl Items, Completion-Rate,
 *   Anzahl High-Priority- und High-Impact-Items und zeigt sie in vier Kennzahlenkarten an.
 *
 * WHY:
 *   Unterstützt Produkt- und Projektplanung, indem es den Fortschritt und die kritischen Items
 *   der Roadmap in einem kompakten Admin-Widget visualisiert.
 *
 * BEHAVIOR:
 *   - Holt alle Roadmap-Items via `useRoadmapItems` und leitet daraus abgeleitete Metriken mit `useMemo` ab.
 *   - Verwendet `WidgetContainer`, um Loading/Error-State und Größe (`config.size`) zu managen.
 *
 * RISKS:
 *   - NOTE: Änderungen an Status- oder Impact-Konventionen (z. B. zusätzliche Statuswerte) erfordern Anpassung der Filter.
 *
 * @returns {JSX.Element} Roadmap metrics widget component
 *
 */
export const RoadmapMetricsWidget: FC = () => {
  const { config } = useWidgetConfig(WIDGET_IDS.ROADMAP_METRICS);
  const { data: roadmapItems, error, isLoading } = useRoadmapItems();

  const metrics = useMemo(() => {
    if (!roadmapItems || roadmapItems.length === 0) {
      return {
        completed: 0,
        completionRate: 0,
        highImpact: 0,
        highPriority: 0,
        total: 0,
      };
    }

    const total = roadmapItems.length;
    const completed = roadmapItems.filter((item) => item.status === "done").length;
    const completionRate = total > 0 ? (completed / total) * 100 : 0;
    const highPriority = roadmapItems.filter((item) => item.priority === "high").length;
    const highImpact = roadmapItems.filter((item) => item.impact_score === "high").length;

    return {
      completed,
      completionRate,
      highImpact,
      highPriority,
      total,
    };
  }, [roadmapItems]);

  return (
    <WidgetContainer
      description="Key statistics"
      error={error ? "Fehler beim Laden der Roadmap Daten" : null}
      icon={<BarChart3 className="w-5 h-5" />}
      loading={isLoading}
      size={config?.size ?? "medium"}
      title="Roadmap Metrics"
      widgetId={WIDGET_IDS.ROADMAP_METRICS}
    >
      <Grid cols={{ lg: 4, sm: 2 }} gap={3}>
        <Card className="text-center" padding="sm" variant="default">
          <Text className="text-xs text-gray-400 mb-1">Total</Text>
          <Heading className="text-xl font-bold text-white" level={4}>
            {metrics.total}
          </Heading>
        </Card>

        <Card className="text-center" padding="sm" variant="default">
          <Text className="text-xs text-gray-400 mb-1">Completion</Text>
          <Heading className="text-xl font-bold text-white" level={4}>
            {metrics.completionRate.toFixed(1)}%
          </Heading>
        </Card>

        <Card className="text-center" padding="sm" variant="default">
          <Text className="text-xs text-gray-400 mb-1">High Priority</Text>
          <Heading className="text-xl font-bold text-white" level={4}>
            {metrics.highPriority}
          </Heading>
        </Card>

        <Card className="text-center" padding="sm" variant="default">
          <Text className="text-xs text-gray-400 mb-1">High Impact</Text>
          <Heading className="text-xl font-bold text-white" level={4}>
            {metrics.highImpact}
          </Heading>
        </Card>
      </Grid>
    </WidgetContainer>
  );
};
